import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, X, ExternalLink, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

// ─────────────────────────────────────────────────────────────
// Popup global: notifica o admin sempre que uma PROPOSTA é ACEITE.
// - Polling /api/propostas a cada 20s, filtra status=aceite
// - Comparar com snapshot anterior (localStorage por utilizador)
// - 1ª carga: marca tudo como visto silenciosamente
// - Quando aparecem IDs novos -> mostra modal verde a celebrar
// ─────────────────────────────────────────────────────────────

const POLL_MS = 20000;
const storageKey = (email) => `meo_seen_aceite_ids_${email || "anon"}`;

export default function NewPropostaAceiteAlert() {
    const { user } = useAuth();
    const [novas, setNovas] = useState([]);
    const seenRef = useRef(null);
    const initRef = useRef(false);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        let cancelled = false;

        try {
            const raw = localStorage.getItem(storageKey(user?.email));
            seenRef.current = new Set(raw ? JSON.parse(raw) : []);
        } catch {
            seenRef.current = new Set();
        }

        const persist = () => {
            try {
                localStorage.setItem(storageKey(user?.email), JSON.stringify(Array.from(seenRef.current)));
            } catch {
                // noop
            }
        };

        const poll = async () => {
            try {
                const { data } = await api.get("/propostas");
                if (cancelled) return;
                const items = (data?.items || []).filter((p) => p.status === "aceite");
                const currentIds = items.map((p) => p.id);
                if (!initRef.current) {
                    // 1ª carga: marca tudo como visto silenciosamente
                    currentIds.forEach((id) => seenRef.current.add(id));
                    persist();
                    initRef.current = true;
                    return;
                }
                const novos = items.filter((p) => !seenRef.current.has(p.id));
                if (novos.length > 0) {
                    setNovas((prev) => {
                        const map = new Map(prev.map((p) => [p.id, p]));
                        novos.forEach((p) => map.set(p.id, p));
                        return Array.from(map.values()).slice(0, 5);
                    });
                    // toca som suave
                    try {
                        const ctx = new (window.AudioContext || window.webkitAudioContext)();
                        const o = ctx.createOscillator();
                        const g = ctx.createGain();
                        o.connect(g);
                        g.connect(ctx.destination);
                        o.frequency.value = 880;
                        g.gain.value = 0.05;
                        o.start();
                        o.stop(ctx.currentTime + 0.15);
                    } catch { /* noop */ }
                    novos.forEach((p) => seenRef.current.add(p.id));
                    persist();
                }
            } catch {
                // silencioso
            }
        };

        poll();
        const id = setInterval(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [user]);

    const dismiss = (id) => {
        setNovas((prev) => prev.filter((p) => p.id !== id));
    };

    const dismissAll = () => setNovas([]);

    if (!novas.length) return null;

    return (
        <div className="fixed top-4 right-4 z-[100] w-[min(94vw,400px)] space-y-2" data-testid="proposta-aceite-alert">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-black text-white px-3 py-1.5 rounded-lg" style={{ background: "linear-gradient(90deg, #10b981, #84cc16)" }}>
                <span>🎉 {novas.length} {novas.length === 1 ? "proposta aceite!" : "propostas aceites!"}</span>
                <button onClick={dismissAll} className="hover:bg-white/20 rounded px-2 py-0.5">Fechar tudo</button>
            </div>
            {novas.map((p) => (
                <div
                    key={p.id}
                    className="bg-white rounded-xl shadow-2xl border-2 border-emerald-400 overflow-hidden"
                    style={{ boxShadow: "0 10px 40px rgba(16,185,129,0.4)" }}
                >
                    <div className="px-4 py-3 flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-lime-500 flex items-center justify-center shrink-0">
                            <CheckCircle2 size={20} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-black uppercase tracking-wider text-emerald-600">Nova adesão!</div>
                            <div className="text-sm font-black text-zinc-900 truncate">{p?.cliente?.nome || "Cliente"}</div>
                            <div className="text-xs text-zinc-600 truncate">
                                {p?.pacote?.nome || "—"} · {Number(p?.pacote?.preco_mensal || 0).toFixed(2).replace(".", ",")}€/mês
                            </div>
                            {p?.cliente?.telefone && (
                                <div className="text-[11px] text-zinc-500 mt-0.5">📱 {p.cliente.telefone}</div>
                            )}
                        </div>
                        <button onClick={() => dismiss(p.id)} className="text-zinc-400 hover:text-zinc-700 p-1 shrink-0">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="flex border-t border-zinc-100">
                        <Link
                            to="/propostas"
                            onClick={() => dismiss(p.id)}
                            className="flex-1 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-1"
                        >
                            <FileText size={12} /> Ver propostas
                        </Link>
                        {p?.kommo_lead_id ? (
                            <a
                                href={`https://salvadordomeo.kommo.com/leads/detail/${p.kommo_lead_id}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => {
                                    try {
                                        navigator.clipboard.writeText(`Olá ${p.cliente?.nome || ""}! 🎉 Recebi a tua adesão ao ${p?.pacote?.nome || "plano"}. Vou já tratar de tudo!`);
                                    } catch { /* noop */ }
                                    dismiss(p.id);
                                }}
                                className="flex-1 px-3 py-2 text-xs font-bold text-white bg-[#002FE6] hover:bg-[#0024B5] flex items-center justify-center gap-1 border-l border-blue-700"
                                title="Abre o lead Kommo e copia mensagem"
                            >
                                🟢 Abrir no Kommo <ExternalLink size={10} />
                            </a>
                        ) : p?.cliente?.telefone && (
                            <a
                                href={`https://wa.me/${(p.cliente.telefone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Olá ${p.cliente.nome || ""}! Recebi a tua adesão. Vou já tratar de tudo!`)}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => dismiss(p.id)}
                                className="flex-1 px-3 py-2 text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center gap-1 border-l border-emerald-600"
                            >
                                💬 WhatsApp <ExternalLink size={10} />
                            </a>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
